import { API_URLS } from '../urls';

/**
 * Messages adressés par un Superadmin à toute la communauté.
 *
 * Il n'y a pas de WebSocket dans admin-front hors de la régie : la remise se
 * fait par relevé périodique. Un message de service n'a pas besoin d'arriver à
 * la milliseconde, et une socket de plus par poste connecté, ouverte en
 * permanence pour trois lignes de texte par semaine, ne se justifierait pas.
 */
export interface CommunityMessage {
  id           : number;
  title        : string;
  message      : string;
  authorName   : string;
  authorId     : number | null;
  /** ISO 8601, tel que rendu par l'API. */
  creationDate : string;
  /** Échéance facultative — null quand le message vit jusqu'à son retrait. */
  endDate      : string | null;
  /** Destinataires choisis, ou `null` pour toute la communauté des créateurs. */
  recipientIds : number[] | null;
  /**
   * « Prioritaire » : en rouge, permanent jusqu'au retrait (pas rangé par
   * « J'ai lu »), et affiché aussi dans les apps Kleon Studio. Absent des
   * réponses d'une API d'avant la 2026-10-10 : lu comme faux.
   */
  priority?    : boolean;
  /** Affiché aussi sous le panneau de connexion de la régie (2026-10-19). */
  showOnLogin? : boolean;
}

/**
 * Les messages vivants pour CE destinataire : ni retirés, ni arrivés à
 * échéance, et diffusés à tous ou nommément ciblés sur `adminId`.
 */
export const fetchCurrentCommunityMessages = async (adminId: number | null): Promise<CommunityMessage[]> => {
  const url = adminId !== null
    ? `${API_URLS.getCurrentCommunityMessages}?admin_id=${adminId}`
    : API_URLS.getCurrentCommunityMessages;

  // `no-store` : sans lui le navigateur sert sa propre copie et le message
  // n'arrive jamais, alors que le relevé tourne bel et bien.
  const res = await fetch(url, { cache: 'no-store' });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const data = await res.json();

  return Array.isArray(data) ? data : [];
};

/**
 * Les messages destinés à la page de connexion : appel anonyme, le serveur ne
 * rend que ceux marqués « page de connexion » et adressés à toute la communauté.
 */
export const fetchLoginCommunityMessages = async (): Promise<CommunityMessage[]> => {
  const res = await fetch(`${API_URLS.getCurrentCommunityMessages}?on_login=1`, { cache: 'no-store' });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const data = await res.json();

  return Array.isArray(data) ? data : [];
};

export interface SendCommunityMessageParams {
  title        : string;
  message      : string;
  authorId     : number | null;
  authorName   : string;
  /** ISO 8601. Absente = le message reste jusqu'à son retrait. */
  endDate?     : string | null;
  /** `null` (ou absent) = toute la communauté des créateurs. */
  recipientIds?: number[] | null;
  /** Message « Prioritaire » : sans échéance, le serveur ignore `endDate`. */
  priority?    : boolean;
  /** Aussi sous le panneau de connexion — seulement pour un message à toute la communauté. */
  showOnLogin? : boolean;
}

export const sendCommunityMessage = async (
  params: SendCommunityMessageParams
): Promise<CommunityMessage> => {
  const res = await fetch(API_URLS.postCommunityMessage, {
    method  : 'POST',
    headers : { 'Content-Type': 'application/json' },
    body    : JSON.stringify({
      title         : params.title,
      message       : params.message,
      author_id     : params.authorId,
      author_name   : params.authorName,
      end_date      : params.endDate ?? '',
      recipient_ids : params.recipientIds ?? null,
      priority      : params.priority ?? false,
      show_on_login : params.showOnLogin ?? false,
    }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) throw new Error(data.error ?? data.detail ?? `HTTP ${res.status}`);

  return data as CommunityMessage;
};

/**
 * Retrait : le message quitte tous les écrans au relevé suivant.
 *
 * La ligne reste en base — c'est une trace de ce qui a été dit à la communauté.
 */
export const retractCommunityMessage = async (id: number): Promise<void> => {
  const res = await fetch(API_URLS.retractCommunityMessage.replace('{id}', String(id)), {
    method  : 'POST',
    headers : { 'Content-Type': 'application/json' },
    body    : '{}',
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error ?? `HTTP ${res.status}`);
  }
};

/**
 * Accusé de lecture, posé quand le destinataire clique « J'ai lu ». Distinct
 * du marquage `localStorage` du poste : c'est la seule trace qui remonte au
 * serveur, celle qui alimente le badge du Superadmin. Volontairement muet en
 * cas d'échec — l'API peut être momentanément absente, et ça ne doit jamais
 * empêcher le destinataire de ranger son message.
 */
export const acknowledgeCommunityMessage = async (id: number, adminId: number | null): Promise<void> => {
  if (adminId === null) return;
  try {
    await fetch(API_URLS.acknowledgeCommunityMessage.replace('{id}', String(id)), {
      method  : 'POST',
      headers : { 'Content-Type': 'application/json' },
      body    : JSON.stringify({ admin_id: adminId }),
    });
  } catch {
    // sans conséquence pour l'écran du destinataire, cf. commentaire ci-dessus
  }
};

export interface SentCommunityMessage {
  id             : number;
  title          : string;
  message        : string;
  creationDate   : string;
  endDate        : string | null;
  retracted      : boolean;
  priority?      : boolean;
  /** Nombre de comptes visés (tous les créateurs, ou la liste choisie). */
  recipientCount : number;
  /** Nombre de ceux qui ont cliqué « J'ai lu ». */
  readCount      : number;
}

/** L'historique de ce qu'un Superadmin a envoyé — retirés et échus compris. */
export const fetchSentCommunityMessages = async (authorId: number): Promise<SentCommunityMessage[]> => {
  const res = await fetch(`${API_URLS.getSentCommunityMessages}?author_id=${authorId}`, { cache: 'no-store' });

  const data = await res.json().catch(() => ([]));

  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);

  return Array.isArray(data) ? data : [];
};

export interface CommunityMessageReadStatus {
  adminId : number;
  name    : string;
  read    : boolean;
  readAt  : string | null;
}

/** Le détail « qui a lu » d'un message, résolu côté serveur. */
export const fetchCommunityMessageReads = async (id: number): Promise<CommunityMessageReadStatus[]> => {
  const res = await fetch(API_URLS.getCommunityMessageReads.replace('{id}', String(id)), { cache: 'no-store' });

  const data = await res.json().catch(() => ([]));

  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);

  return Array.isArray(data) ? data : [];
};
